/**
 * Market screen - main betting interface
 */

import { useEffect, useState, useCallback } from "preact/hooks";
import { route } from "preact-router";
import { useStore } from "../store";
import { useWebSocket } from "../hooks/useWebSocket";
import { api } from "../api/client";
import type { Bet, User, FeedItem } from "../api/types";
import { BetDetailModal } from "../components/BetDetailModal";
import { CreateBetModal, CreateBetData } from "../components/CreateBetModal";

interface MarketProps {
  marketId: string;
}

type Tab = "bets" | "feed" | "leaderboard" | "settings";

export function Market({ marketId }: MarketProps) {
  const {
    market,
    user,
    bets,
    setBets,
    users,
    setUsers,
    setMarket,
    setUser,
    currentMarketId,
    currentUserId,
    setSession,
    theme,
    setTheme,
    addToast,
  } = useStore();
  const [loading, setLoading] = useState(true);
  const [selectedBet, setSelectedBet] = useState<Bet | null>(null);
  const [detailBet, setDetailBet] = useState<Bet | null>(null);
  const [showCreateBet, setShowCreateBet] = useState(false);
  const [wagerAmount, setWagerAmount] = useState(10);
  const [wagerSide, setWagerSide] = useState<"YES" | "NO">("YES");
  const [placingWager, setPlacingWager] = useState(false);
  const [error, setError] = useState("");
  const [restoring, setRestoring] = useState(!market || !user);
  const [activeTab, setActiveTab] = useState<Tab>("bets");
  const [leaderboard, setLeaderboard] = useState<
    {
      user: User;
      profit: number;
      rank: number;
      inBets?: number;
      totalValue?: number;
    }[]
  >([]);
  const [feed, setFeed] = useState<FeedItem[]>([]);

  // Always fetch fresh market data when screen loads
  useEffect(() => {
    const storedMarketId = currentMarketId;
    const storedUserId = currentUserId;

    // If we have a user in memory for this market, just refresh market data
    if (user && user.marketId === marketId) {
      api
        .getMarket(marketId)
        .then((marketData) => {
          setMarket(marketData);
          setRestoring(false);
        })
        .catch((err) => {
          console.error("Failed to fetch market:", err);
          setSession(null, null);
          route("/");
        });
      return;
    }

    // Try to restore from persisted session
    if (storedMarketId === marketId && storedUserId) {
      // Restore session from API
      Promise.all([api.getMarket(marketId), api.getUser(storedUserId)])
        .then(([marketData, userData]) => {
          setMarket(marketData);
          setUser(userData);
          setRestoring(false);
        })
        .catch((err) => {
          console.error("Failed to restore session:", err);
          setSession(null, null);
          route("/");
        });
    } else {
      setRestoring(false);
      route("/");
    }
  }, [marketId]);

  // Function to refresh bets
  const refreshBets = useCallback(() => {
    if (!user) return;
    api.getBets(marketId, user.id).then(setBets).catch(console.error);
  }, [marketId, user, setBets]);

  // Function to refresh leaderboard
  const refreshLeaderboard = useCallback(() => {
    api
      .getLeaderboard(marketId)
      .then((data) => {
        setLeaderboard(data.users);
        setUsers(data.users.map((entry) => entry.user));
      })
      .catch(console.error);
  }, [marketId, setUsers]);

  // Function to refresh feed
  const refreshFeed = useCallback(() => {
    api
      .getFeed(marketId)
      .then((data) => setFeed(data.feed))
      .catch(console.error);
  }, [marketId]);

  // Load bets, users, and feed
  useEffect(() => {
    if (!user) return;

    Promise.all([
      api.getBets(marketId, user.id),
      api.getLeaderboard(marketId),
      api.getFeed(marketId),
    ])
      .then(([betsData, leaderboardData, feedData]) => {
        setBets(betsData);
        setLeaderboard(leaderboardData.users);
        setUsers(leaderboardData.users.map((entry) => entry.user));
        setFeed(feedData.feed);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [marketId, user, setBets, setUsers]);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback(
    (msg: { type: string; data: unknown }) => {
      console.log("WebSocket message:", msg.type, msg.data);

      if (msg.type === "bet_created" || msg.type === "bet_approved") {
        refreshBets();
      }
      if (msg.type === "wager_placed") {
        refreshBets();
        refreshLeaderboard();
        refreshFeed();
      }
      if (msg.type === "bet_resolved") {
        refreshBets();
        refreshLeaderboard();
      }
    },
    [refreshBets, refreshLeaderboard, refreshFeed],
  );

  // WebSocket for real-time updates
  useWebSocket(marketId, user?.id ?? null, {
    onMessage: handleWebSocketMessage,
  });

  const handlePlaceWager = async () => {
    if (!selectedBet || !user) return;

    setPlacingWager(true);
    setError("");

    try {
      await api.placeWager(selectedBet.id, user.id, {
        side: wagerSide,
        amount: wagerAmount,
      });

      // Refresh user balance and bets
      refreshBets();
      refreshLeaderboard();

      // Refresh user data to get updated balance
      const updatedUser = await api.getUser(user.id);
      setUser(updatedUser);

      // Show success toast
      addToast({
        message: `Placed $${wagerAmount} on ${wagerSide}`,
        type: "success",
        duration: 3000,
      });

      setSelectedBet(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to place wager");
    } finally {
      setPlacingWager(false);
    }
  };

  const handleCreateBet = async (data: CreateBetData) => {
    if (!user) return;

    await api.createBet(marketId, user.id, {
      description: data.description,
      subjectUserId: user.id,
      openingWager: data.initialPool,
    });

    // Refresh bets and user balance
    refreshBets();
    const updatedUser = await api.getUser(user.id);
    setUser(updatedUser);
  };

  const handleResolveBet = async (betId: string, outcome: "YES" | "NO") => {
    if (!user) return;

    try {
      await api.resolveBet(betId, user.id, outcome === "YES");
      refreshBets();
      refreshLeaderboard();
      setDetailBet(null);
    } catch (err) {
      console.error("Failed to resolve bet:", err);
    }
  };

  const handleDeleteBet = async (betId: string) => {
    if (!user) return;

    try {
      await api.deleteBet(betId, user.id);
      refreshBets();
      refreshLeaderboard();
      const updatedUser = await api.getUser(user.id);
      setUser(updatedUser);
      setDetailBet(null);
    } catch (err) {
      console.error("Failed to delete bet:", err);
    }
  };

  const calculateProbability = (bet: Bet): number => {
    const total = bet.yesPool + bet.noPool;
    if (total === 0) return 0.5;
    return bet.yesPool / total;
  };

  const calculateMaxWager = (bet: Bet): number => {
    const currentPool = bet.yesPool + bet.noPool;
    // 25% of resulting pool = currentPool / 3
    return Math.max(100, Math.floor(currentPool / 3));
  };

  if (restoring || !market || !user) {
    return (
      <div class="screen market">
        <p>Loading...</p>
      </div>
    );
  }

  const activeBets = bets.filter((b) => b.status === "active");
  const resolvedBets = bets.filter(
    (b) => b.status === "resolved_yes" || b.status === "resolved_no",
  );

  return (
    <div class="screen market">
      {/* Header */}
      <header class="market-header">
        {/* Title, logo, and balance row */}
        <div class="header-info-row">
          <h1 class="market-title">{market.name}</h1>
          <img src="/icon-512.png" alt="Cazino" class="header-logo" />
          <div class="user-balance">
            <span class="balance-amount">${user.balance}</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <nav class="market-tabs">
          <button
            class={`tab ${activeTab === "bets" ? "active" : ""}`}
            onClick={() => setActiveTab("bets")}
          >
            Bets
          </button>
          <button
            class={`tab ${activeTab === "feed" ? "active" : ""}`}
            onClick={() => setActiveTab("feed")}
          >
            Feed
          </button>
          <button
            class={`tab ${activeTab === "leaderboard" ? "active" : ""}`}
            onClick={() => setActiveTab("leaderboard")}
          >
            Leaderboard
          </button>
          <button
            class={`tab ${activeTab === "settings" ? "active" : ""}`}
            onClick={() => setActiveTab("settings")}
          >
            Settings
          </button>
        </nav>

        {/* Scrolling Ticker */}
        {feed.length > 0 && (
          <div class="feed-ticker">
            <div class="ticker-track">
              {/* Render content twice for seamless infinite loop */}
              {[0, 1].map((copy) => (
                <div class="ticker-content" key={copy}>
                  {feed.slice(0, 10).map((item) => (
                    <span class="ticker-item" key={`${copy}-${item.id}`}>
                      {item.type === "resolution" ? (
                        <>
                          <span class="resolution-badge">
                            Resolved {item.outcome}
                          </span>
                          &nbsp;{item.bet?.description}
                        </>
                      ) : (
                        <>
                          <span class="user-mention">
                            {item.user?.avatar} {item.user?.displayName}
                          </span>
                          &nbsp;bet&nbsp;
                          <span class="amount">${item.amount}</span>
                          &nbsp;on&nbsp;
                          <span class={`side ${item.side.toLowerCase()}`}>
                            {item.side}
                          </span>
                          &nbsp;-&nbsp;{item.bet?.description}
                        </>
                      )}
                      <span class="ticker-separator">•</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Tab Content */}
      <div class="tab-content">
        {activeTab === "bets" && (
          <>
            {loading ? (
              <p>Loading bets...</p>
            ) : (
              <>
                {activeBets.length > 0 && (
                  <section class="bets-section">
                    <h3>Active Bets</h3>
                    <div class="bets-list">
                      {activeBets.map((bet) => (
                        <div
                          key={bet.id}
                          class={`bet-card ${selectedBet?.id === bet.id ? "selected" : ""}`}
                          onClick={() => setDetailBet(bet)}
                        >
                          <div class="bet-description">{bet.description}</div>
                          <div class="bet-probability">
                            <div class="prob-bar">
                              <div
                                class="prob-yes"
                                style={{
                                  width: `${calculateProbability(bet) * 100}%`,
                                }}
                              />
                            </div>
                            <div class="prob-labels">
                              <span>
                                YES{" "}
                                {Math.round(calculateProbability(bet) * 100)}%
                              </span>
                              <span>
                                NO{" "}
                                {Math.round(
                                  (1 - calculateProbability(bet)) * 100,
                                )}
                                %
                              </span>
                            </div>
                          </div>
                          <div class="bet-pools">
                            <span>Pool: ${bet.yesPool + bet.noPool}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {resolvedBets.length > 0 && (
                  <section class="bets-section resolved-bets">
                    <h3>Resolved</h3>
                    <div class="bets-list">
                      {resolvedBets.map((bet) => (
                        <div key={bet.id} class="bet-card resolved">
                          <div class="bet-description">{bet.description}</div>
                          <div class="bet-outcome">
                            {bet.status === "resolved_yes" ? "YES" : "NO"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {activeBets.length === 0 && resolvedBets.length === 0 && (
                  <div class="empty-state">
                    <p>No bets yet. Create one to get started!</p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === "feed" && (
          <div class="feed-section">
            {feed.length === 0 ? (
              <div class="empty-state">
                <p>No activity yet. Place a wager to get started!</p>
              </div>
            ) : (
              <div class="feed-list">
                {feed.map((item) =>
                  item.type === "resolution" ? (
                    <div key={item.id} class="feed-item feed-item-resolution">
                      <div class="feed-item-header">
                        <span class="resolution-badge">
                          Resolved {item.outcome}
                        </span>{" "}
                        {item.bet?.description}
                      </div>
                      <div class="feed-item-payouts">
                        {item.payouts.map((p) => (
                          <div key={p.user?.id} class="payout-row">
                            <span class="payout-user">
                              {p.user?.avatar} {p.user?.displayName}
                            </span>
                            <span
                              class={`payout-amount ${p.profit >= 0 ? "positive" : "negative"}`}
                            >
                              {p.profit >= 0 ? "+" : ""}
                              {p.profit}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div class="feed-item-details">
                        {new Date(item.resolvedAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  ) : (
                    <div key={item.id} class="feed-item">
                      <div class="feed-item-header">
                        <span class="user-mention">
                          {item.user?.avatar} {item.user?.displayName}
                        </span>
                        {" bet "}
                        <span class="amount">${item.amount}</span>
                        {" on "}
                        <span class={`side ${item.side.toLowerCase()}`}>
                          {item.side}
                        </span>
                        {" - "}
                        {item.bet?.description}
                      </div>
                      <div class="feed-item-details">
                        {new Date(item.placedAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "leaderboard" && (
          <div class="leaderboard-section">
            <div class="leaderboard-list">
              {leaderboard.map((entry, idx) => (
                <div
                  key={entry.user.id}
                  class={`leaderboard-row ${entry.user.id === user.id ? "current-user" : ""}`}
                >
                  <span class="rank">#{idx + 1}</span>
                  <span class="avatar">{entry.user.avatar}</span>
                  <span class="name">{entry.user.displayName}</span>
                  <span class="total-value">
                    ${entry.totalValue ?? entry.user.balance}
                  </span>
                  <span
                    class={`profit ${entry.profit >= 0 ? "positive" : "negative"}`}
                  >
                    {entry.profit >= 0 ? "+$" : "-$"}
                    {Math.abs(entry.profit)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <div class="settings-section">
            <h3>Settings</h3>

            {/* Appearance Settings - Available to all users */}
            <div class="settings-group">
              <div class="settings-group-title">Appearance</div>
              <div class="settings-item">
                <div class="settings-item-label">
                  <span class="settings-item-title">Theme</span>
                  <span class="settings-item-description">
                    Choose your preferred color scheme
                  </span>
                </div>
                <div class="theme-toggle">
                  <button
                    class={`theme-toggle-btn ${theme === "light" ? "active" : ""}`}
                    onClick={() => setTheme("light")}
                  >
                    <span class="theme-icon">☀️</span>
                    Light
                  </button>
                  <button
                    class={`theme-toggle-btn ${theme === "dark" ? "active" : ""}`}
                    onClick={() => setTheme("dark")}
                  >
                    <span class="theme-icon">🌙</span>
                    Dark
                  </button>
                </div>
              </div>
            </div>

            {/* Admin Controls - Only visible to admins */}
            {user.isAdmin && (
              <>
                <div class="settings-divider" />
                <div class="settings-group">
                  <div class="settings-group-title">Admin Controls</div>
                  <p class="hint">
                    Market Status: <strong>{market.status}</strong>
                  </p>
                  <p class="hint">
                    Invite Code: <strong>{market.inviteCode}</strong>
                  </p>

                  <div class="admin-actions">
                    {market.status === "draft" && (
                      <button
                        class="btn btn-primary"
                        onClick={async () => {
                          await api.openMarket(marketId, user.id);
                          const updatedMarket = await api.getMarket(marketId);
                          setMarket(updatedMarket);
                        }}
                      >
                        Open Market
                      </button>
                    )}
                    {market.status === "open" && (
                      <button
                        class="btn btn-secondary"
                        onClick={async () => {
                          await api.closeMarket(marketId, user.id);
                          const updatedMarket = await api.getMarket(marketId);
                          setMarket(updatedMarket);
                        }}
                      >
                        Close Market
                      </button>
                    )}
                  </div>

                  {activeBets.length > 0 && (
                    <div class="admin-bets">
                      <h4>Resolve Bets</h4>
                      <div class="bets-list">
                        {activeBets.map((bet) => (
                          <div key={bet.id} class="bet-card">
                            <div class="bet-description">{bet.description}</div>
                            <div class="button-group">
                              <button
                                class="btn btn-small"
                                onClick={() => handleResolveBet(bet.id, "YES")}
                              >
                                Resolve YES
                              </button>
                              <button
                                class="btn btn-small btn-secondary"
                                onClick={() => handleResolveBet(bet.id, "NO")}
                              >
                                Resolve NO
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Bet Detail Modal */}
      {detailBet && (
        <BetDetailModal
          bet={detailBet}
          currentUserId={user.id}
          isAdmin={user.isAdmin}
          onClose={() => setDetailBet(null)}
          onPlaceWager={() => {
            setSelectedBet(detailBet);
            setDetailBet(null);
          }}
          onResolve={(outcome) => handleResolveBet(detailBet.id, outcome)}
          onDelete={() => handleDeleteBet(detailBet.id)}
        />
      )}

      {/* Create Bet Modal */}
      {showCreateBet && (
        <CreateBetModal
          users={users}
          onClose={() => setShowCreateBet(false)}
          onCreate={handleCreateBet}
        />
      )}

      {/* Wager Modal */}
      {selectedBet && (
        <div class="modal-overlay" onClick={() => setSelectedBet(null)}>
          <div class="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Place Wager</h3>
            <p>{selectedBet.description}</p>

            <div class="wager-form">
              <div class="side-picker">
                <button
                  class={`side-btn ${wagerSide === "YES" ? "selected" : ""}`}
                  onClick={() => setWagerSide("YES")}
                >
                  YES
                </button>
                <button
                  class={`side-btn ${wagerSide === "NO" ? "selected" : ""}`}
                  onClick={() => setWagerSide("NO")}
                >
                  NO
                </button>
              </div>

              <div class="amount-picker">
                <label>Amount:</label>
                <input
                  type="number"
                  value={wagerAmount}
                  onInput={(e) => setWagerAmount(Number(e.currentTarget.value))}
                  min={1}
                  max={Math.min(user.balance, calculateMaxWager(selectedBet))}
                />
                <span class="form-hint">
                  Max: ${Math.min(user.balance, calculateMaxWager(selectedBet))}
                </span>
              </div>

              {error && <div class="error">{error}</div>}

              <div class="modal-actions">
                <button
                  class="btn btn-secondary"
                  onClick={() => setSelectedBet(null)}
                >
                  Cancel
                </button>
                <button
                  class="btn btn-primary"
                  onClick={handlePlaceWager}
                  disabled={
                    placingWager ||
                    wagerAmount > user.balance ||
                    wagerAmount > calculateMaxWager(selectedBet)
                  }
                >
                  {placingWager ? "Placing..." : "Place Wager"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fixed bottom Create Bet button */}
      <div class="bottom-action">
        <button
          class="btn btn-primary btn-create-bet"
          onClick={() => setShowCreateBet(true)}
        >
          + Create Bet
        </button>
      </div>
    </div>
  );
}
